// Libraries
import { action, computed, observable } from "mobx";

// Core
import { FieldType } from "Core/Utils/Utils";
import { ViewModelBase } from "Core/ViewModels";

// Custom
import { Server } from "Custom/Globals/AppUrls";
import { ReservationAndRelatedModel, ReservationAndRelatedModelDTO } from "Custom/Models/DashboardModels/ReservationAndRelatedModel";
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";
import { Format } from "Custom/Utils";
import { IndustryContactModel } from "Custom/Models/IndustryContactModel";
import { PostFormWithFile } from "Core/Utils/Axios";
import moment from "moment";
import { StoresInstance } from "Custom/Stores";
import { GlobalSettingType } from "Custom/Models/Enums/GlobalSettingType";
import { SignatureStatusType, SignatureRequestStatusType } from "Custom/Models/Enums/SignatureStatusType";
import { ReservationSignatureStatusModel } from "Custom/Models/DashboardModels/ReservationSignatureStatusModel";
import { ReservationType1FinancialDTO } from "Custom/Models/DashboardModels/ReservationType1FinancialModel";
import { ReservationSignatureStatusDTO } from "Custom/Models/DashboardModels/ReservationSignatureStatusModel";
import { ApiResult } from "Core/Models/ApiResult";
import { isNullOrUndefined } from "util";
import { currencyFormat } from "Custom/Utils/Format";
import { ReservationType1FinancialIncentiveTypesModel } from "Custom/Models/ReservationType1FinancialIncentiveTypesModel";

export class ReservationType1ViewModel extends ViewModelBase<ReservationAndRelatedModel> {
    constructor(propertyId: string) {
        super(new ReservationAndRelatedModel());
        this.setDecorators(ReservationAndRelatedModel);

        this.propertyId = propertyId;
        this.loggedInUserSignatureRole = StoresInstance.domain.UserStore ? StoresInstance.domain.UserStore.userSignatureRole : "";

        if (propertyId !== null && propertyId !== undefined && propertyId !== "") {
            this.loadReservationType1AndRelated(propertyId);
        }
    }

    //#region Properties
    @observable
    public propertyId: string = "";

    // #region Properties
    @observable
    public loggedInUserSignatureRole: string = "";

    @observable
    public apiStatus: ApiStatus = ApiStatus.loading;

    @observable
    private _signatureDate: moment.Moment | null = null;
    // #endregion Properties

    // #region Computeds
    @computed
    public get ApiStatus(): ApiStatus {
        return this.apiStatus;
    }

    @computed
    public get purchaserOneSignatureURL(): string | null {
        if (this.model && this.model.financial && this.model.financial.purchaserOneSignatureDataUrl) {
            return this.model.financial.purchaserOneSignatureDataUrl;
        }
        return null;
    }

    @computed
    public get purchaserTwoSignatureURL(): string | null {
        if (this.model && this.model.financial && this.model.financial.purchaserTwoSignatureDataUrl) {
            return this.model.financial.purchaserTwoSignatureDataUrl;
        }
        return null;
    }

    @computed
    get depositAmount(): number | null {
        if (this.model === null || this.model === undefined) {
            return null;
        }

        const financial = this.model.financial;
        if (financial === null || financial === undefined) {
            return null;
        }

        const agreedPriceFormatted = financial.agreedPrice ?? "0";
        const percentageDeposit = financial.percentageDeposit ?? "0";
        const amountDepositFormatted = financial.amountDeposit ?? "0";

        if (
            ((percentageDeposit === null || percentageDeposit === undefined) && amountDepositFormatted === "") ||
            agreedPriceFormatted === null ||
            agreedPriceFormatted === undefined ||
            agreedPriceFormatted === ""
        ) {
            return null;
        }

        const agreedPrice = Number(Format.currencyFormatForUpsert(agreedPriceFormatted));
        const amountDeposit = Number(Format.currencyFormatForUpsert(amountDepositFormatted));
        const percentageDepositValue = Number(percentageDeposit);

        if (isNaN(agreedPrice)) {
            return null;
        }

        return financial.amountDeposit && financial.amountDeposit.toString().length > 0 ? amountDeposit : (percentageDepositValue / 100) * agreedPrice;
    }

    @computed
    get balanceDepositDue(): number | null {
        if (this.model === null || this.model === undefined) {
            return null;
        }

        const depositAmount = this.depositAmount;
        const earlyBird = this.model.earlyBird;
        const reservation = this.model.reservation;

        if (depositAmount === null) {
            return null;
        }

        const fullFee = Number(Format.currencyFormatForUpsert(reservation && reservation.reservationFeePaid ? reservation.reservationFeePaid : "0"));
        const earlyBirdFee = Number(Format.currencyFormatForUpsert(earlyBird && earlyBird.reservationFeePaid ? earlyBird.reservationFeePaid : "0"));

        return depositAmount - (earlyBirdFee + fullFee);
    }

    @computed
    get depositValue(): string | null {
        if (
            this.model === null ||
            this.model === undefined ||
            this.model.financial === null ||
            this.model.financial === undefined ||
            isNullOrUndefined(this.model.reservation.reservationFeePaid)
        ) {
            return null;
        }

        let fullFee = Format.currencyFormatForUpsert(isNullOrUndefined(this.model.reservation.reservationFeePaid) ? "0" : this.model.reservation.reservationFeePaid);
        let fullFeeValue = Number(this.model.reservation.reservationFeePaid);

        let earlyBirdFee = Format.currencyFormatForUpsert(isNullOrUndefined(this.model.earlyBird.reservationFeePaid) ? "0" : this.model.earlyBird.reservationFeePaid);
        let earlyBirdFeeValue = Number(earlyBirdFee);

        let depositValue = fullFeeValue + earlyBirdFeeValue;
        return depositValue.toString();
    }

    @computed
    get lenderContact(): IndustryContactModel | undefined {
        if (!this.model || !this.model.industryContacts || !this.model.funding) {
            return undefined;
        }

        if (this.model.funding.lenderFinancialId) {
            return this.model.industryContacts.find(ic => ic.id === this.model.funding.lenderFinancialId);
        }

        return undefined;
    }

    @computed
    get lenderContacts() {
        if (!this.model || !this.model.funding) {
            return undefined;
        }

        if (this.model.fundingContacts) {
            return this.model.fundingContacts.filter(ic => ic.reservationType1FundingId === this.model.funding.id && ic.isLender);
        }

        return undefined;
    }

    @computed
    get financialAdvisorContact(): IndustryContactModel | undefined {
        if (!this.model || !this.model.industryContacts || !this.model.funding) {
            return undefined;
        }

        if (this.model.funding.financialAdvisorId) {
            return this.model.industryContacts.find(ic => ic.id === this.model.funding.financialAdvisorId);
        }

        return undefined;
    }

    @computed
    get financialAdvisorContacts() {
        if (!this.model || !this.model.funding) {
            return undefined;
        }

        if (this.model.fundingContacts) {
            return this.model.fundingContacts.filter(ic => ic.reservationType1FundingId === this.model.funding.id && !ic.isLender);
        }

        return undefined;
    }

    @computed
    get sellerContact(): IndustryContactModel | undefined {
        if (!this.model || !this.model.industryContacts || !this.model.solicitor) {
            return undefined;
        }

        if (this.model.solicitor.sellerSolicitorId) {
            return this.model.industryContacts.find(ic => ic.id === this.model.solicitor.sellerSolicitorId);
        }

        return undefined;
    }

    @computed
    get sellerSolicitorContacts() {
        if (!this.model || !this.model.solicitor) {
            return undefined;
        }

        if (this.model.solicitorContacts) {
            return this.model.solicitorContacts.filter(ic => ic.reservationType1SolicitorId === this.model.solicitor.id && ic.isSeller);
        }

        return undefined;
    }

    @computed
    get purchaserContact(): IndustryContactModel | undefined {
        if (!this.model || !this.model.industryContacts || !this.model.solicitor) {
            return undefined;
        }

        if (this.model.solicitor.purchaserSolicitorId) {
            return this.model.industryContacts.find(ic => ic.id === this.model.solicitor.purchaserSolicitorId);
        }

        return undefined;
    }

    @computed
    get purchaserSolicitorContacts() {
        if (!this.model || !this.model.solicitor) {
            return undefined;
        }

        if (this.model.solicitorContacts) {
            return this.model.solicitorContacts.filter(ic => ic.reservationType1SolicitorId === this.model.solicitor.id && !ic.isSeller);
        }

        return undefined;
    }

    @computed
    get isPurchaserOneLoggedIn(): boolean {
        if (this.loggedInUserSignatureRole == "purchaserTwo") return false;
        return true;
    }

    @computed
    public get legalCopy() {
        const setting = StoresInstance.domain.AccountStore.getSettingByType(GlobalSettingType.ReservationType1LegalCopy);

        if (setting) {
            return setting.settingValue;
        }

        return "";
    }

    @computed
    public get financialIncentive() {
        if (!this.model || !this.model.financial) {
            return undefined;
        }

        const incentiveType = this.model.reservationType1FinancialIncentiveTypes.find(fit => fit.id === this.model.financial.incentive);

        return incentiveType ? incentiveType.displayName : undefined;
    }

    @computed
    public get financialAdditionalIncentives() {
        if (!this.model) {
            return [];
        }

        const incentiveTypes = this.model.reservationType1FinancialIncentiveTypes;

        return (this.model.financialAdditionalIncentives || []).map(incentive => {
            const incentiveType = incentiveTypes.find(fit => fit.id === incentive.incentive);
            console.log("incentiveType", incentiveType);

            return {
                id: incentive.id,
                reservationType1FinancialId: incentive.reservationType1FinancialId,
                incentive: incentive.incentive,
                otherDetails: incentive.otherDetails,
                amount: incentive.amount,
                displayName: incentiveType ? incentiveType.displayName : incentive.incentive,
            };
        });
    }

    @computed
    public get signatureStatus(): ReservationSignatureStatusModel | null {
        return this.model?.signatureStatus || null;
    }

    @computed
    public get isSignatureRequestActive(): boolean {
        const status = this.signatureStatus;
        return status ? status.isActive && status.requestStatus === SignatureRequestStatusType.Published : false;
    }

    @computed
    public get isSignatureRequestExpired(): boolean {
        const status = this.signatureStatus;
        if (!status || !status.expiryDate) return false;
        return moment().isAfter(moment(status.expiryDate));
    }

    @computed
    public get daysUntilExpiry(): number | null {
        const status = this.signatureStatus;
        if (!status || !status.expiryDate) return null;
        const expiry = moment(status.expiryDate);
        const now = moment();
        return Math.max(0, expiry.diff(now, "days"));
    }

    @computed
    public get purchaserOneSignatureStatus(): SignatureStatusType {
        const status = this.signatureStatus;
        if (!status) return SignatureStatusType.Pending;

        if (this.isSignatureRequestExpired) {
            return SignatureStatusType.Expired;
        }

        return status.purchaserOneStatus;
    }

    @computed
    public get purchaserTwoSignatureStatus(): SignatureStatusType {
        const status = this.signatureStatus;
        if (!status) return SignatureStatusType.Pending;

        if (this.isSignatureRequestExpired) {
            return SignatureStatusType.Expired;
        }

        return status.purchaserTwoStatus;
    }

    @computed
    public get salesAgentSignatureStatus(): SignatureStatusType {
        const status = this.signatureStatus;
        if (!status) return SignatureStatusType.Pending;

        if (this.isSignatureRequestExpired) {
            return SignatureStatusType.Expired;
        }

        return status.salesAgentStatus;
    }

    @computed
    public get allSignaturesComplete(): boolean {
        return (
            this.purchaserOneSignatureStatus === SignatureStatusType.Complete && this.purchaserTwoSignatureStatus === SignatureStatusType.Complete
            // this.salesAgentSignatureStatus === SignatureStatusType.Complete
        );
    }

    @computed
    public get currentUserSignatureComplete(): boolean {
        if (this.isPurchaserOneLoggedIn) {
            return this.purchaserOneSignatureStatus === SignatureStatusType.Complete;
        } else {
            return this.purchaserTwoSignatureStatus === SignatureStatusType.Complete;
        }
    }

    @computed
    public get pendingSignatureCount(): number {
        let count = 0;
        if (this.purchaserOneSignatureStatus === SignatureStatusType.Pending) count++;
        if (this.purchaserTwoSignatureStatus === SignatureStatusType.Pending) count++;
        if (this.salesAgentSignatureStatus === SignatureStatusType.Pending) count++;
        return count;
    }

    @computed
    public get awaitingSignatureFrom(): string {
        const pending = [];
        if (this.purchaserOneSignatureStatus === SignatureStatusType.Pending) pending.push("purchaser 1");
        if (this.purchaserTwoSignatureStatus === SignatureStatusType.Pending) pending.push("purchaser 2");
        if (this.salesAgentSignatureStatus === SignatureStatusType.Pending) pending.push("sales agent");

        if (pending.length === 0) return "All signatures complete";

        if (pending.length === 1) return `Your all done, were still awaiting a signature from ${pending[0]}.`;
        return `Your all done, were still awaiting a signature from ${pending.join(", ")}.`;
    }

    @computed
    public get awaitingSignatureAdditionalInfo(): string {
        const pending = [];
        if (this.purchaserOneSignatureStatus === SignatureStatusType.Pending) pending.push("purchaser 1");
        if (this.purchaserTwoSignatureStatus === SignatureStatusType.Pending) pending.push("purchaser 2");
        if (this.salesAgentSignatureStatus === SignatureStatusType.Pending) pending.push("sales agent");

        if (pending.length === 0) return "";
        return "Once they have added their signature a signed copy of the form can be found in the documents sections of the myHome portal.";
    }

    @computed
    public get linkExpiryInfo(): string {
        const status = this.signatureStatus;
        if (!status || !status.expiryDate) return "";

        const expiry = moment(status.expiryDate);
        const timeStr = expiry.format("HH:mm");
        const dateStr = expiry.format("DD/MM/YY");

        return `The link expires at ${timeStr} on the ${dateStr}`;
    }

    // #endregion Computeds

    public navigateToSignatures = (propertyId: string) => {
        this.history.push(`/signatures/${propertyId}`);
    };

    // #region Actions
    @action
    public setApiStatus(apiStatus: ApiStatus) {
        this.apiStatus = apiStatus;
    }

    // @action setPurchaserOneSignatureDate(value: string) {
    //     if (this.model && this.model.financial) {
    //         this.model.financial.purchaserOneSignatureDate = value;
    //     }
    // }

    // @action setPurchaserTwoSignatureDate(value: string) {
    //     if (this.model && this.model.financial) {
    //         this.model.financial.purchaserOneSignatureDate = value;
    //     }
    // }

    @action
    public async submitPurchaserSignature(dataUrl: string): Promise<void> {
        if (!this.model || !this.model.financial) return;

        const f = this.model.financial;

        // Update only the logged-in purchaser
        if (this.isPurchaserOneLoggedIn) {
            f.purchaserOneSignatureDataUrl = dataUrl;
            if (f.purchaserOneSignatureDataUrl) {
                await this.uploadSignature(f.purchaserOneSignatureDataUrl, val => (f.purchaserOneSignatureUrl = val));
            }
        } else {
            f.purchaserTwoSignatureDataUrl = dataUrl;
            if (f.purchaserTwoSignatureDataUrl) {
                await this.uploadSignature(f.purchaserTwoSignatureDataUrl, val => (f.purchaserTwoSignatureUrl = val));
            }
        }

        await this.upsertReservationType1Financial();

        // Update signature status
        const signatureType = this.isPurchaserOneLoggedIn ? "purchaserOne" : "purchaserTwo";
        await this.updateSignatureStatusAfterSigning(signatureType);
    }

    @action
    public async upsertReservationType1Financial(): Promise<void> {
        if (!this.model || !this.model.financial || !this.model.reservation) return;

        try {
            this.setApiStatus(ApiStatus.loading);

            const f = this.model.financial;

            // Only include the signature of the logged-in user
            const payload = {
                id: f.id,
                reservationId: f.reservationId,

                purchaserOneSignatureUrl: f.purchaserOneSignatureUrl,
                purchaserOneSignatureDataUrl: "",
                purchaserOneSignatureDate: f.purchaserOneSignatureDate
                    ? moment(f.purchaserOneSignatureDate)
                          .format()
                          .toLocaleString()
                    : "",

                purchaserTwoSignatureUrl: f.purchaserTwoSignatureUrl,
                purchaserTwoSignatureDataUrl: "",
                purchaserTwoSignatureDate: f.purchaserTwoSignatureDate
                    ? moment(f.purchaserTwoSignatureDate)
                          .format()
                          .toLocaleString()
                    : "",

                salesAgentSignatureUrl: f.salesAgentSignatureUrl,
                salesAgentSignatureDataUrl: "",
                salesAgentSignatureDate: f.salesAgentSignatureDate
                    ? moment(f.salesAgentSignatureDate)
                          .format()
                          .toLocaleString()
                    : "",
            };

            const apiResult = await this.Post<ReservationType1FinancialDTO>(Server.Api.ReservationType1.UpsertReservationType1Financial, payload);

            if (apiResult.wasSuccessful && apiResult.payload) {
                f.fromDto(apiResult.payload);
                this.loadReservationType1AndRelated(this.propertyId);
                this.setApiStatus(ApiStatus.success);
            } else {
                this.setApiStatus(ApiStatus.error);
            }
        } catch (error) {
            this.setApiStatus(ApiStatus.error);
        }
    }

    @action
    public async emailSignedReservationCopy(): Promise<void> {
        try {
            await this.Post<ApiResult>(Server.Api.ReservationType1.EmailSignedReservationCopy, { reservationId: this.model?.financial?.reservationId });
        } catch (error) {
            console.error("Failed to email signed reservation copy:", error);
        }
    }

    @action
    public async loadSignatureStatus(): Promise<void> {
        if (!this.model?.reservation?.id) return;

        try {
            const apiResult = await this.Get<ReservationSignatureStatusModel>(Server.Api.ReservationType1.GetSignatureStatus + "/" + this.model.reservation.id);
            if (apiResult.wasSuccessful && apiResult.payload) {
                this.model.signatureStatus.fromDto(apiResult.payload);
            }
        } catch (error) {
            console.error("Failed to load signature status:", error);
        }
    }

    @action
    public async upsertSignatureStatus(): Promise<void> {
        if (!this.model?.signatureStatus || !this.model?.reservation?.id) return;

        try {
            const payload = this.model.signatureStatus.toDto();
            const apiResult = await this.Post<ReservationSignatureStatusDTO>(Server.Api.ReservationType1.UpsertSignatureStatus, payload);
            if (apiResult.wasSuccessful && apiResult.payload) {
                this.model.signatureStatus.fromDto(apiResult.payload);
            }
        } catch (error) {
            console.error("Failed to save signature status:", error);
        }
    }

    @action
    public async publishSignatureRequest(): Promise<void> {
        if (!this.model?.reservation?.id) return;

        try {
            const payload = {
                reservationId: this.model.reservation.id,
                expiryDays: 7,
            };
            const apiResult = await this.Post<ReservationSignatureStatusDTO>(Server.Api.ReservationType1.PublishSignatureRequest, payload);
            if (apiResult.wasSuccessful && apiResult.payload) {
                this.model.signatureStatus.fromDto(apiResult.payload);
            }
        } catch (error) {
            console.error("Failed to publish signature request:", error);
        }
    }

    @action
    public async republishSignatureRequest(): Promise<void> {
        if (!this.model || !this.model.reservation || !this.model.reservation.id) return;

        try {
            const payload = {
                reservationId: this.model.reservation.id,
                expiryDays: 7,
            };
            const apiResult = await this.Post<ReservationSignatureStatusDTO>(Server.Api.ReservationType1.PublishSignatureRequest, payload);
            if (apiResult.wasSuccessful && apiResult.payload) {
                this.model.signatureStatus.fromDto(apiResult.payload);
            }
        } catch (error) {
            console.error("Failed to republish signature request:", error);
        }
    }

    @action
    public async updateSignatureStatusAfterSigning(signatureType: "purchaserOne" | "purchaserTwo" | "salesAgent"): Promise<void> {
        if (!this.model?.signatureStatus) return;

        const status = this.model.signatureStatus;
        const now = moment().format();

        switch (signatureType) {
            case "purchaserOne":
                status.purchaserOneStatus = SignatureStatusType.Complete;
                status.purchaserOneSignedDate = now;
                break;
            case "purchaserTwo":
                status.purchaserTwoStatus = SignatureStatusType.Complete;
                status.purchaserTwoSignedDate = now;
                break;
            case "salesAgent":
                status.salesAgentStatus = SignatureStatusType.Complete;
                status.salesAgentSignedDate = now;
                break;
        }

        // Check if all required signatures are complete
        if (this.allSignaturesComplete) {
            status.requestStatus = SignatureRequestStatusType.Completed;
            status.completedDate = now;
        }

        await this.upsertSignatureStatus();
    }
    private loadReservationType1AndRelated = async (id: string): Promise<void> => {
        this.setApiStatus(ApiStatus.loading);

        try {
            const apiResult = await this.Get<ReservationAndRelatedModelDTO>(Server.Api.ReservationType1.GetReservationType1AndRelated + "/" + id);

            if (apiResult.wasSuccessful) {
                const reservationAndRelatedModel: ReservationAndRelatedModelDTO = apiResult.payload;

                this.model.fromDto(reservationAndRelatedModel);
                this.setApiStatus(ApiStatus.success);
            } else {
                this.setApiStatus(ApiStatus.error);
            }
        } catch (exception) {
            // this.setApiStatus(ApiStatus.error);
        } finally {
            // Finally
        }
    };

    private dataUrlToFile(dataUrl: string, filename = "signature.png"): File {
        const [header, base64] = dataUrl.split(",");
        const mime = header.match(/:(.*?);/)![1];
        const binary = atob(base64);
        const array = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            array[i] = binary.charCodeAt(i);
        }
        return new File([array], filename, { type: mime });
    }

    private async uploadSignature(dataUrl: string, assignUrl: (val: string) => void) {
        const file = this.dataUrlToFile(dataUrl);
        const uploadResult = await PostFormWithFile<any>(Server.Api.Image.Upload, file);

        assignUrl(uploadResult.payload);
    }

    // #endregion Actions

    // #region Boilerplate
    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    public isFieldValid(fieldName: keyof FieldType<any>): boolean {
        return true;
    }
    // #endregion Boilerplate
}
